// S08/S09 Run detail + S10 drill-down + S11 Customers + S14 Rates
import React, { useState as useStateRD, useEffect as useEffectRD, useMemo as useMemoRD } from 'react';
import { Icon, Pill, Button, Card, Money, Banner, Section, Select, Input } from './ui.jsx';
import { REPS, RATES, RUNS, INVOICES, getRunTotals, getRepTotals, sumBy } from './data.jsx';
import { api } from './api.js';
import { hasPermission } from './auth.jsx';
import { recalculateRun } from './admin.jsx';

// ===================== S08/S09 · Run Detail =====================
export function ScreenRunDetail({ onNav, onOpenInvoice, currentUser }) {
  const [run, setRun]           = useStateRD(null);
  const [invoices, setInvoices] = useStateRD([]);
  const [reps, setReps]         = useStateRD([]);
  const [users, setUsers]       = useStateRD([]); // all users, for lookups
  const [plans, setPlans]       = useStateRD([]); // all plans, for showing rep plan names
  const [customers, setCustomers] = useStateRD([]); // for resolving invoice.customerId -> name
  const [loading, setLoading]   = useStateRD(true);
  const [expanded, setExpanded] = useStateRD(null);
  // 'Hide unassigned' toggle for the All invoices table — defaults to true
  // so the table looks clean. Unassigned invoices reappear automatically
  // when their customer gets a rep + Recalculate runs.
  const [hideUnassigned, setHideUnassigned] = useStateRD(true);
  const [confirm, setConfirm]   = useStateRD(false);
  const [approveModal, setApproveModal] = useStateRD(false);
  const [unapproveModal, setUnapproveModal] = useStateRD(false);
  const [deleteModal, setDeleteModal] = useStateRD(false);
  const [busy, setBusy]         = useStateRD(false);

  // Read the desired run id from the URL (?id=…); fall back to "latest"
  // for legacy direct navigation to /commission-runs/detail with no id.
  const runIdParam = (() => {
    try { return new URLSearchParams(window.location.search).get('id'); }
    catch { return null; }
  })();

  useEffectRD(() => {
    let alive = true;
    (async () => {
      try {
        const runs = await api.runs.list();
        const target = runIdParam ? runs.find(r => r.id === runIdParam) : runs[0];
        if (!target) { if (alive) setLoading(false); return; }
        const [invs, usersList, plansList, custList] = await Promise.all([
          api.invoices.listByRun(target.id),
          api.users.list(),
          api.commissionPlans.list(),
          api.customers.list(),
        ]);
        if (!alive) return;
        setRun(target);
        setInvoices(invs);
        setUsers(usersList);
        setReps(usersList.filter(u => u.role === 'rep' && u.active));
        setPlans(plansList);
        setCustomers(custList);
      } catch (err) {
        console.error('Failed to load run:', err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [runIdParam]);

  if (loading) return <div className="p-10 text-center text-neutral-500 text-[13px]">Loading run…</div>;
  if (!run)   return <Banner tone="info" icon="info" title="No runs yet">Create a new run to see detail here.</Banner>;

  // DB rows use .grossProfit and .repUserId; adapter normalizes to legacy shape for helpers.
  // Effective commission: rejected invoices earn the rep $0 regardless of the
  // calculated value. We keep the original commission visible (preRejectionCommission)
  // so the drawer + audit can show the strikethrough; the totals helpers in
  // data.jsx see only `commission`, so zeroing it here flows everywhere.
  //
  // Held-for-collection (cash-basis plans, unpaid invoices): commission is
  // already stored as 0 in DB. We re-derive the "would-be" amount from
  // net × rate (or base × rate for non-Y1/Y2 plans) so the drawer can show
  // a strikethrough and a "released when paid" message.
  const adapted = invoices.map(i => {
    const wouldBeIfPaid = i.heldForCollection
      ? Math.round((Number(i.net) || 0) * (Number(i.rate) || 0) * 100) / 100
      : null;
    return {
      ...i,
      gp: i.grossProfit,
      repId: i.repUserId,
      repUserId: i.repUserId,
      preRejectionCommission: i.commission,
      // Excluded invoices stay in the run but don't contribute to totals.
      // Rejected (approver) and held (cash-basis) also zero out the
      // commission used by getRunTotals / getRepTotals.
      commission: (i.rejected || i.excluded) ? 0 : i.commission,
      heldWouldBeCommission: wouldBeIfPaid,
    };
  });
  const totals = getRunTotals(adapted);
  // New flow: Draft → Finalized → Approved
  //   Draft     = still calculating, admin can edit
  //   Finalized = math locked, ready for approver to review
  //   Approved  = approver signed off, rep sees it as their real number
  const isDraft   = run.status === 'draft';
  const finalized = run.status === 'finalized';
  const approved  = run.status === 'approved';

  const userById = new Map(users.map(u => [u.id, u]));
  // Customer name resolution: invoice rows store customer_id, the human
  // name lives on public.customers. Without this lookup the Client column
  // would fall back to the entity (staffing supplier) name, which is wrong.
  const customerById = new Map(customers.map(c => [c.id, c]));
  const clientNameFor = (inv) => {
    const c = inv.customerId ? customerById.get(inv.customerId) : null;
    return c?.name || inv.entity || inv.branch || '—';
  };
  const creator  = run.createdBy ? userById.get(run.createdBy) : null;
  const approver = run.approvedBy ? userById.get(run.approvedBy) : null;
  const finalizer = run.finalizedBy ? userById.get(run.finalizedBy) : null;
  const isCreator = currentUser && run.createdBy === currentUser.id;
  // Approval happens AFTER finalize, so the relevant "can approve" check is on finalized runs.
  // Two layers: (1) the user's role must grant runs.approve, and (2) segregation
  // of duties — they can't approve their own work. Same applies to per-invoice
  // rejection (separate permission so an "Approver-but-no-rejection" role is
  // expressible if needed).
  const canApprove = finalized && !isCreator && hasPermission(currentUser, 'runs', 'approve');
  const canRejectIndividual = finalized && !isCreator && hasPermission(currentUser, 'runs', 'reject_invoice');

  // ---------- Per-invoice rejection (approver only, while run is finalized) ----------
  // Same trust model as run approval: the approver can't be the run creator.
  // RLS already restricts updates to admins; this guards the UI.
  const canRejectInvoices = canRejectIndividual;

  // Re-run the full calculation (rep assignment + tier + rate + commission
  // + quota gate + payment basis) using the latest customer→rep mapping
  // and plan settings. Used when the admin changes plans / rep assignments
  // after the initial wizard pass.
  const doRecalculate = async () => {
    setBusy(true);
    try {
      await recalculateRun(run.id);
      // Re-fetch invoices so the by-rep totals reflect the new numbers.
      const fresh = await api.invoices.listByRun(run.id);
      setInvoices(fresh);
    } catch (err) {
      alert('Recalculate failed: ' + (err.message || err));
    } finally { setBusy(false); }
  };

  // Toggle excluded on a single invoice. Excluded invoices stay in the
  // run for audit but their commission doesn't count toward the rep's
  // totals (engine + display both zero them). Admins use this for clearly
  // wrong / duplicate / refunded invoices that shouldn't be paid out.
  const toggleExcludeInvoice = async (invoiceId, makeExcluded) => {
    const updated = await api.invoices.update(invoiceId, { excluded: !!makeExcluded });
    setInvoices(prev => prev.map(i => i.id === invoiceId ? updated : i));
  };

  const rejectInvoice = async (invoiceId, reason) => {
    if (!canRejectInvoices) return;
    const updated = await api.invoices.reject(invoiceId, reason, currentUser.id);
    setInvoices(prev => prev.map(i => i.id === invoiceId ? updated : i));
  };
  const unrejectInvoice = async (invoiceId) => {
    if (!canRejectInvoices) return;
    const updated = await api.invoices.unreject(invoiceId);
    setInvoices(prev => prev.map(i => i.id === invoiceId ? updated : i));
  };

  // Approve / Unapprove actions
  const doApprove = async () => {
    setBusy(true);
    try {
      const updated = await api.runs.approve(run.id, currentUser.id);
      setRun(updated);
      setApproveModal(false);
    } catch (err) { alert('Approve failed: ' + err.message); }
    finally { setBusy(false); }
  };
  // "Unlock" steps the run back one state:
  //   approved  → finalized  (remove approval, keep it locked)
  //   finalized → draft      (remove lock, back to editable)
  const doUnapprove = async (reason) => {
    setBusy(true);
    try {
      let patch;
      if (run.status === 'approved') {
        patch = { status: 'finalized', approvedAt: null, approvedBy: null, rejectionReason: reason };
      } else {
        patch = { status: 'draft', finalizedAt: null, finalizedBy: null, rejectionReason: reason };
      }
      const updated = await api.runs.update(run.id, patch);
      setRun(updated);
      setUnapproveModal(false);
    } catch (err) { alert('Unlock failed: ' + err.message); }
    finally { setBusy(false); }
  };

  // Permanent delete (draft only). RLS double-checks status='draft' on the server.
  const doDelete = async () => {
    setBusy(true);
    try {
      await api.runs.delete(run.id);
      // Navigate back to the list — the run is gone.
      onNav('s03');
    } catch (err) {
      alert('Delete failed: ' + err.message);
      setBusy(false);
    }
  };

  // Compute per-rep totals from the actual DB users (not the legacy REPS constant).
  const repStats = reps.map(rep => {
    const mine = adapted.filter(i => i.repUserId === rep.id);
    return {
      rep,
      count: mine.length,
      totalBill:  mine.reduce((a, i) => a + i.totalBill, 0),
      gp:         mine.reduce((a, i) => a + i.gp, 0),
      commission: mine.reduce((a, i) => a + (i.excluded ? 0 : i.commission), 0),
      unpaid:     mine.filter(i => !i.paid).reduce((a, i) => a + i.outstanding, 0),
      invoices:   mine,
    };
  }).filter(r => r.count > 0);
  const unassignedCount = adapted.filter(i => !i.repUserId).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <div className="flex items-center gap-3 text-[12px] text-neutral-500">
            <button className="hover:text-primary-700" onClick={() => onNav('s03')}>Commission runs</button>
            <span>/</span>
            <span className="font-mono">run-2026-03</span>
          </div>
          <div className="flex items-center gap-3 mt-1">
            <h1 className="text-[24px] font-bold text-neutral-900 tracking-[-0.01em]">{run.period}</h1>
            <Pill tone={finalized ? 'finalized' : approved ? 'paid' : 'draft'}>{run.status}</Pill>
          </div>
          <div className="text-[12px] text-neutral-500 mt-1.5">
            {creator ? <>Created by {creator.name} · </> : null}
            {invoices.length} invoices
            {approver ? <> · Approved by {approver.name} on {run.approvedAt?.slice(0, 10)}</> : null}
            {finalized && run.finalizedAt ? <> · Finalized {run.finalizedAt.slice(0, 10)}</> : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="md" icon="download">Export ▾</Button>
          {/* Delete is allowed on draft AND finalized runs (RLS enforces).
              Approved runs require unapproval first — by design. */}
          {(isDraft || finalized) && hasPermission(currentUser, 'runs', 'delete') && (
            <Button variant="ghost" size="md" icon="x" onClick={() => setDeleteModal(true)}
              className="text-red-700 hover:bg-red-50">
              Delete run
            </Button>
          )}
          {isDraft && hasPermission(currentUser, 'runs', 'edit') && (
            <Button variant="secondary" size="md" icon="sparkle" onClick={doRecalculate} disabled={busy}>
              {busy ? 'Recalculating…' : 'Recalculate'}
            </Button>
          )}
          {isDraft && hasPermission(currentUser, 'runs', 'finalize') && (
            <>
              <Button variant="success" size="md" icon="lock" onClick={() => setConfirm(true)}>Finalize run</Button>
            </>
          )}
          {finalized && (
            <>
              <Button variant="ghost" size="md" icon="unlock" onClick={() => setUnapproveModal(true)}>Unlock back to draft</Button>
              {canApprove ? (
                <Button variant="success" size="md" icon="check" onClick={() => setApproveModal(true)}>Approve run</Button>
              ) : isCreator ? (
                <Button variant="secondary" size="md" icon="lock" disabled title="You created this run — a different admin must approve it.">
                  Approve (needs other admin)
                </Button>
              ) : null}
            </>
          )}
          {approved && (
            <Button variant="ghost" size="md" icon="unlock" onClick={() => setUnapproveModal(true)}>Unlock</Button>
          )}
        </div>
      </div>

      {/* Banner */}
      {approved ? (
        <Banner tone="success" icon="check" title="Approved — ready for payroll">
          Approved by <b>{approver?.name || 'an admin'}</b> on {run.approvedAt?.slice(0, 10)}. Reps see this commission as their confirmed number.
        </Banner>
      ) : finalized ? (
        <Banner tone="info" icon="lock" title="Finalized — awaiting approval">
          Locked by <b>{finalizer?.name || 'an admin'}</b> on {run.finalizedAt?.slice(0, 10)}. {isCreator
            ? 'You created this run, so a different admin needs to approve it.'
            : canApprove
              ? 'Review the numbers below and click Approve when everything looks right.'
              : 'Waiting on approver sign-off before reps see it as final.'}
        </Banner>
      ) : (
        <>
          <Banner tone="info" icon="info" title="Draft run · safe to edit">
            Override or exclude individual invoices with a required reason. Finalize when the numbers are locked in — approval comes after.
          </Banner>
          {run.rejectionReason && (
            <Banner tone="warn" icon="warn" title="Previously unlocked from approval">
              Reason: {run.rejectionReason}
            </Banner>
          )}
        </>
      )}

      {/* Carryover summary — only shown when carryover lines exist */}
      {(() => {
        const carryovers = adapted.filter(i => i.isCarryover);
        if (carryovers.length === 0) return null;
        const total = carryovers.reduce((a, i) => a + Number(i.commission || 0), 0);
        return (
          <Banner tone="success" icon="check" title={`${carryovers.length} carryover${carryovers.length === 1 ? '' : 's'} from previous runs — $${total.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}>
            These invoices were billed earlier but held under a cash-basis plan because the customer hadn't paid. They've cleared in this period's AR, so the commission is released here.
          </Banner>
        );
      })()}

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-4">
        <Card padding="p-5" className="border-t-[3px] border-t-primary-700">
          <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Total commission</div>
          <div className="mt-1 text-[26px] font-bold font-mono tabular-nums text-neutral-900 leading-none">${totals.commission.toLocaleString('en-US', {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
          <div className="text-[12px] text-neutral-500 mt-1.5">Across {REPS.filter(r => getRepTotals(invoices, r.id).count > 0).length} reps</div>
        </Card>
        <Card padding="p-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Total bill</div>
          <div className="mt-1 text-[22px] font-bold font-mono tabular-nums text-neutral-900 leading-none">${totals.totalBill.toLocaleString('en-US', {maximumFractionDigits:0})}</div>
          <div className="text-[12px] text-neutral-500 mt-1.5">Billed to clients</div>
        </Card>
        <Card padding="p-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Gross profit</div>
          <div className="mt-1 text-[22px] font-bold font-mono tabular-nums text-neutral-900 leading-none">${totals.gp.toLocaleString('en-US', {maximumFractionDigits:0})}</div>
          <div className="text-[12px] text-neutral-500 mt-1.5">Before ACA/SICK</div>
        </Card>
        <Card padding="p-5" className={totals.unpaid > 0 ? 'border-amber-300' : ''}>
          <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Unpaid AR</div>
          <div className="mt-1 text-[22px] font-bold font-mono tabular-nums text-amber-700 leading-none">${totals.unpaid.toLocaleString('en-US', {maximumFractionDigits:0})}</div>
          <div className="text-[12px] text-neutral-500 mt-1.5">{invoices.filter(i => !i.paid).length} invoices outstanding</div>
        </Card>
      </div>

      {/* By-rep table */}
      <Section title="By sales rep" sub="Click a row to expand invoices. Click any invoice for the full calculation.">
        {unassignedCount > 0 && (
          <Banner tone="warn" icon="warn" title={`${unassignedCount} invoices have no rep assigned`}>
            Open the New Run wizard and use the Assign step to link customers to reps. Those invoices don't count toward anyone's dashboard.
          </Banner>
        )}
        <Card padding="p-0">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-neutral-500 bg-canvas/60 border-b border-neutral-200">
                <th className="px-4 py-2.5 font-semibold w-8"/>
                <th className="px-4 py-2.5 font-semibold">Sales rep</th>
                <th className="px-4 py-2.5 font-semibold text-right">Invoices</th>
                <th className="px-4 py-2.5 font-semibold text-right">Total Bill</th>
                <th className="px-4 py-2.5 font-semibold text-right">GP</th>
                <th className="px-4 py-2.5 font-semibold text-right">Commission</th>
                <th className="px-4 py-2.5 font-semibold text-right">Unpaid</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {repStats.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-[13px] text-neutral-500">
                  No reps have invoices in this run yet. Assign customers to reps in the wizard.
                </td></tr>
              )}
              {repStats.map(({ rep, count, totalBill, gp, commission, unpaid, invoices: repInvoices }) => {
                const t = { count, totalBill, gp, commission, unpaid };
                const isOpen = expanded === rep.id;
                // Resolve this rep's effective commission plan name.
                // commissionPlanId on the user wins; otherwise the tenant's default plan.
                const assignedPlan = rep.commissionPlanId ? plans.find(p => p.id === rep.commissionPlanId) : null;
                const defaultPlan  = plans.find(p => p.isDefault);
                const planName = assignedPlan?.name || defaultPlan?.name || 'No plan';
                const planIsAssigned = !!assignedPlan;
                const hasRateOverride = rep.y1Rate != null || rep.y2Rate != null;
                return (
                  <React.Fragment key={rep.id}>
                    <tr className="hover:bg-primary-100/30 cursor-pointer" onClick={() => setExpanded(isOpen ? null : rep.id)}>
                      <td className="px-4 py-3 text-neutral-400"><Icon name={isOpen ? 'chevronDown' : 'chevron'} size={14}/></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-6 h-6 rounded-full bg-neutral-100 text-neutral-700 flex items-center justify-center text-[10px] font-bold">{rep.name.split(' ').map(s=>s[0]).join('')}</div>
                          <div>
                            {/* Rep name is clickable — opens the admin "view as" profile. */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const path = `/rep/this-month?as=${rep.id}`;
                                window.history.pushState({}, '', path);
                                window.dispatchEvent(new PopStateEvent('popstate'));
                              }}
                              className="font-semibold text-neutral-900 hover:text-primary-700 hover:underline text-left"
                              title="Open this rep's profile">
                              {rep.name}
                            </button>
                            <div className="text-[10px] text-neutral-500">
                              <span className="font-medium">{planName}</span>
                              {!planIsAssigned && <span className="text-neutral-400"> (default)</span>}
                              {hasRateOverride && (
                                <span className="ml-1 font-mono text-amber-700">
                                  · override Y1 {rep.y1Rate != null ? (rep.y1Rate * 100).toFixed(2) + '%' : '—'} / Y2+ {rep.y2Rate != null ? (rep.y2Rate * 100).toFixed(2) + '%' : '—'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">{t.count}</td>
                      <td className="px-4 py-3 text-right"><Money value={t.totalBill}/></td>
                      <td className="px-4 py-3 text-right"><Money value={t.gp}/></td>
                      <td className="px-4 py-3 text-right"><Money value={t.commission} className="font-semibold text-neutral-900"/></td>
                      <td className="px-4 py-3 text-right"><Money value={t.unpaid} className={t.unpaid > 0 ? 'text-amber-700' : ''}/></td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={7} className="bg-canvas/50 p-0">
                          <div className="px-4 py-2">
                            <table className="w-full text-[12.5px]">
                              <thead>
                                <tr className="text-[10.5px] uppercase tracking-[0.06em] text-neutral-500">
                                  <th className="px-3 py-2 font-semibold text-left">Invoice</th>
                                  <th className="px-3 py-2 font-semibold text-left">Client</th>
                                  <th className="px-3 py-2 font-semibold text-left">Date</th>
                                  <th className="px-3 py-2 font-semibold text-right">Total Bill</th>
                                  <th className="px-3 py-2 font-semibold text-right">GP</th>
                                  <th className="px-3 py-2 font-semibold text-center">Rate</th>
                                  <th className="px-3 py-2 font-semibold text-right">Commission</th>
                                  <th className="px-3 py-2 font-semibold text-center">Status</th>
                                  <th className="px-3 py-2 w-8"/>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-neutral-200">
                                {repInvoices.slice(0, 10).map(inv => (
                                  <tr key={inv.id} className={`hover:bg-white cursor-pointer ${inv.excluded ? 'opacity-40' : ''} ${inv.rejected ? 'bg-red-50/40' : ''}`}
                                      onClick={() => onOpenInvoice({
                                        ...inv,
                                        gp: inv.grossProfit, date: inv.invoiceDate,
                                        doc: inv.branch + '/' + inv.invoiceNumber,
                                        client: clientNameFor(inv),
                                        // Approver-action context (consumed by InvoiceDrawer):
                                        _canReject: canRejectInvoices,
                                        _onReject: rejectInvoice,
                                        _onUnreject: unrejectInvoice,
                                        _onToggleExclude: toggleExcludeInvoice,
                                        _canExclude: isDraft,
                                      })}>
                                    <td className="px-3 py-2 font-mono text-neutral-700">
                                      {inv.branch}/{inv.invoiceNumber}
                                      {inv.isCarryover && <Pill tone="info" className="ml-2 normal-case tracking-normal">carryover</Pill>}
                                    </td>
                                    <td className="px-3 py-2 text-neutral-800 truncate max-w-[180px]">{clientNameFor(inv)}</td>
                                    <td className="px-3 py-2 font-mono text-neutral-500">{inv.invoiceDate}</td>
                                    <td className="px-3 py-2 text-right"><Money value={inv.totalBill}/></td>
                                    <td className="px-3 py-2 text-right"><Money value={inv.grossProfit}/></td>
                                    <td className="px-3 py-2 text-center"><Pill tone={inv.tier === 'y1' ? 'y1' : 'y2'}>{inv.tier === 'y1' ? 'Y1 · 5%' : 'Y2+ · 7%'}</Pill></td>
                                    <td className="px-3 py-2 text-right">
                                      {inv.overridden && <span className="text-[10px] text-primary-700 font-semibold mr-1">OVR</span>}
                                      {inv.rejected ? (
                                        <span>
                                          <span className="text-[11px] font-mono text-neutral-400 line-through mr-1.5">${(inv.preRejectionCommission ?? 0).toFixed(2)}</span>
                                          <Money value={0} className="font-semibold"/>
                                        </span>
                                      ) : inv.heldForCollection ? (
                                        <span>
                                          <span className="text-[11px] font-mono text-neutral-400 line-through mr-1.5">${Number(inv.heldWouldBeCommission || 0).toFixed(2)}</span>
                                          <Money value={0} className="font-semibold"/>
                                        </span>
                                      ) : (
                                        <Money value={inv.commission} className="font-semibold"/>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      {inv.excluded ? <Pill tone="neutral">excluded</Pill>
                                        : inv.rejected ? <Pill tone="error">rejected</Pill>
                                        : inv.heldForCollection ? <Pill tone="warn">held</Pill>
                                        : inv.paid ? <Pill tone="paid">paid</Pill>
                                        : <Pill tone="unpaid">unpaid · {inv.ageDays}d</Pill>}
                                    </td>
                                    <td className="px-3 py-2 text-neutral-300"><Icon name="kebab" size={14}/></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {repInvoices.length > 10 && (
                              <div className="py-2 text-center text-[12px] text-neutral-500">+ {repInvoices.length - 10} more invoices — <button className="text-primary-700 font-medium">view all</button></div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Card>
      </Section>

      {/* All invoices — DB-backed, with a 'Hide unassigned' toggle so
          customers without a rep don't clutter the run view. Once they
          get assigned (Customers screen) + Recalculate runs, they reappear. */}
      {(() => {
        const visibleInvoices = hideUnassigned
          ? adapted.filter(i => i.repUserId)
          : adapted;
        const unassignedCount = adapted.filter(i => !i.repUserId).length;
        const sectionTitle = hideUnassigned && unassignedCount > 0
          ? `All invoices · ${visibleInvoices.length} of ${invoices.length}`
          : `All invoices · ${invoices.length}`;
        return (
      <Section title={sectionTitle}
        sub="Direct view of every invoice in this run. Click any row to open the calculation drawer."
        right={unassignedCount > 0 ? (
          <label className="flex items-center gap-2 text-[12px] text-neutral-700 cursor-pointer">
            <input type="checkbox" checked={hideUnassigned}
              onChange={e => setHideUnassigned(e.target.checked)}
              className="w-4 h-4 accent-primary-700"/>
            Hide {unassignedCount} unassigned invoice{unassignedCount === 1 ? '' : 's'}
          </label>
        ) : null}>
        {visibleInvoices.length === 0 ? (
          <Banner tone="info" icon="info" title={hideUnassigned && unassignedCount > 0 ? 'All invoices are unassigned' : 'No invoices in this run yet'}>
            {hideUnassigned && unassignedCount > 0
              ? <>All {unassignedCount} invoices are awaiting a customer-to-rep assignment. Uncheck the filter to see them, or go to <button onClick={() => onNav('s11')} className="text-primary-700 hover:text-primary-900 font-medium">Customers</button> to assign reps, then Recalculate this run.</>
              : 'Upload a Transaction file via the wizard to populate this run.'}
          </Banner>
        ) : (
          <Card padding="p-0">
            <div className="max-h-[600px] overflow-y-auto">
              <table className="w-full text-[13px]">
                <thead className="sticky top-0 bg-canvas/90 backdrop-blur">
                  <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-neutral-500 border-b border-neutral-200">
                    <th className="px-4 py-2.5 font-semibold">Invoice</th>
                    <th className="px-4 py-2.5 font-semibold">Client</th>
                    <th className="px-4 py-2.5 font-semibold">Date</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Total Bill</th>
                    <th className="px-4 py-2.5 font-semibold text-right">GP</th>
                    <th className="px-4 py-2.5 font-semibold text-center">Rate</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Commission</th>
                    <th className="px-4 py-2.5 font-semibold text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {visibleInvoices.slice(0, 200).map(inv => (
                    <tr key={inv.id} className={`hover:bg-primary-100/30 cursor-pointer ${inv.rejected ? 'bg-red-50/50' : ''}`} onClick={() => onOpenInvoice({
                        ...inv,
                        gp: inv.grossProfit, date: inv.invoiceDate,
                        doc: inv.branch + inv.invoiceNumber,
                        client: clientNameFor(inv),
                        _canReject: canRejectInvoices,
                        _onReject: rejectInvoice,
                        _onUnreject: unrejectInvoice,
                        _onToggleExclude: toggleExcludeInvoice,
                        _canExclude: isDraft,
                      })}>
                      <td className="px-4 py-2.5 font-mono text-neutral-700">
                        {inv.branch}/{inv.invoiceNumber}
                        {inv.isCarryover && <Pill tone="info" className="ml-2 normal-case tracking-normal">carryover</Pill>}
                      </td>
                      <td className="px-4 py-2.5 text-neutral-700 truncate max-w-[200px]">{clientNameFor(inv)}</td>
                      <td className="px-4 py-2.5 font-mono text-neutral-500">{inv.invoiceDate}</td>
                      <td className="px-4 py-2.5 text-right"><Money value={inv.totalBill}/></td>
                      <td className="px-4 py-2.5 text-right"><Money value={inv.grossProfit}/></td>
                      <td className="px-4 py-2.5 text-center"><Pill tone={inv.tier === 'y1' ? 'y1' : 'y2'}>{inv.tier?.toUpperCase() || '—'}</Pill></td>
                      <td className="px-4 py-2.5 text-right">
                        {inv.rejected ? (
                          <span>
                            <span className="text-[11px] font-mono text-neutral-400 line-through mr-1.5">${(inv.preRejectionCommission ?? 0).toFixed(2)}</span>
                            <Money value={0} className="font-semibold"/>
                          </span>
                        ) : inv.heldForCollection ? (
                          <span>
                            <span className="text-[11px] font-mono text-neutral-400 line-through mr-1.5">${Number(inv.heldWouldBeCommission || 0).toFixed(2)}</span>
                            <Money value={0} className="font-semibold"/>
                          </span>
                        ) : (
                          <Money value={inv.commission} className="font-semibold"/>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {inv.excluded ? <Pill tone="neutral">excluded</Pill>
                          : inv.rejected ? <Pill tone="error">rejected</Pill>
                          : inv.heldForCollection ? <Pill tone="warn">held</Pill>
                          : inv.paid ? <Pill tone="paid">paid</Pill>
                          : <Pill tone="unpaid">unpaid</Pill>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {visibleInvoices.length > 200 && (
              <div className="px-4 py-3 border-t border-neutral-200 text-[12px] text-neutral-500 text-center">
                Showing first 200 of {visibleInvoices.length.toLocaleString()} invoices{hideUnassigned && unassignedCount > 0 ? ` (${unassignedCount} unassigned hidden)` : ''}.
              </div>
            )}
          </Card>
        )}
      </Section>
        );
      })()}

      {/* Approve modal */}
      {approveModal && <ApproveModal run={run} totals={totals} repStats={repStats} busy={busy} onCancel={() => setApproveModal(false)} onConfirm={doApprove}/>}

      {/* Unapprove modal */}
      {unapproveModal && <UnapproveModal run={run} busy={busy} onCancel={() => setUnapproveModal(false)} onConfirm={doUnapprove}/>}

      {/* Delete-run modal */}
      {deleteModal && <DeleteRunModal run={run} invoiceCount={invoices.length} busy={busy} onCancel={() => setDeleteModal(false)} onConfirm={doDelete}/>}

      {/* Finalize confirmation modal */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 p-6">
          <div className="bg-white rounded-lg shadow-xl max-w-[540px] w-full overflow-hidden">
            <div className="px-6 py-5 border-b border-neutral-200">
              <h3 className="text-[16px] font-semibold text-neutral-900">Finalize {run?.period || 'this'} run?</h3>
              <div className="text-[13px] text-neutral-500 mt-1">After finalizing, this run becomes read-only. An Unlock action requires a reason and is logged.</div>
            </div>
            <div className="px-6 py-4 bg-canvas space-y-1.5 text-[13px]">
              <div className="flex justify-between"><span className="text-neutral-500">Reps included</span><span className="font-mono">{REPS.filter(r => getRepTotals(invoices, r.id).count > 0).length}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Invoices</span><span className="font-mono">{invoices.filter(i=>!i.excluded).length}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Total commission</span><Money value={totals.commission} className="font-semibold"/></div>
            </div>
            <div className="px-6 py-4 flex items-center justify-end gap-2 border-t border-neutral-200">
              <Button variant="ghost" onClick={() => setConfirm(false)}>Cancel</Button>
              <Button variant="success" icon="lock" onClick={async () => {
                try {
                  const updated = await api.runs.update(run.id, {
                    status: 'finalized',
                    finalizedAt: new Date().toISOString(),
                    finalizedBy: currentUser?.id || null,
                    totals: { totalBill: totals.totalBill, gp: totals.gp, commission: totals.commission },
                  });
                  setRun(updated);
                } finally { setConfirm(false); }
              }}>Confirm & finalize</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===================== S10 · Invoice drawer =====================
function ApproveModal({ run, totals, repStats, busy, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 p-6">
      <div className="bg-white rounded-lg shadow-xl max-w-[540px] w-full overflow-hidden">
        <div className="px-6 py-5 border-b border-neutral-200">
          <h3 className="text-[16px] font-semibold text-neutral-900">Approve {run.period}?</h3>
          <div className="text-[13px] text-neutral-500 mt-1">
            Approving makes these numbers visible to reps as their confirmed commission. You can send it back to draft later if needed.
          </div>
        </div>
        <div className="px-6 py-4 bg-canvas space-y-1.5 text-[13px]">
          <div className="flex justify-between"><span className="text-neutral-500">Reps included</span><span className="font-mono">{repStats.length}</span></div>
          <div className="flex justify-between"><span className="text-neutral-500">Total commission</span><Money value={totals.commission} className="font-semibold"/></div>
          <div className="flex justify-between"><span className="text-neutral-500">Approver</span><span className="font-semibold text-neutral-900">You</span></div>
        </div>
        <div className="px-6 py-4 flex items-center justify-end gap-2 border-t border-neutral-200">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant="success" icon="check" onClick={onConfirm} disabled={busy}>{busy ? 'Approving…' : 'Confirm approval'}</Button>
        </div>
      </div>
    </div>
  );
}

function UnapproveModal({ run, busy, onCancel, onConfirm }) {
  const [reason, setReason] = useStateRD('');
  // Context-sensitive: approved → finalized, finalized → draft
  const toState = run.status === 'approved' ? 'finalized' : 'draft';
  const title = run.status === 'approved'
    ? `Unlock ${run.period} approval?`
    : `Unlock ${run.period} back to draft?`;
  const helper = run.status === 'approved'
    ? 'The run will return to "finalized" status. Reps will see their dashboard revert to "pending approval." Reason is logged.'
    : 'The run will return to draft. The calculation can be re-edited. Reps revert to "still being finalized." Reason is logged.';
  const cta = run.status === 'approved' ? 'Remove approval' : 'Unlock to draft';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 p-6">
      <div className="bg-white rounded-lg shadow-xl max-w-[520px] w-full overflow-hidden">
        <div className="px-6 py-5 border-b border-neutral-200">
          <h3 className="text-[16px] font-semibold text-neutral-900">{title}</h3>
          <div className="text-[13px] text-neutral-500 mt-1">{helper}</div>
        </div>
        <div className="px-6 py-4 space-y-2">
          <label className="block text-[12px] font-semibold text-neutral-700">Reason</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
            placeholder="e.g. Rate for Marisol was wrong — needs to be 5% not 7%."
            className="w-full px-3 py-2 text-[13px] rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 resize-none"/>
        </div>
        <div className="px-6 py-4 flex items-center justify-end gap-2 border-t border-neutral-200">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={() => onConfirm(reason.trim())} disabled={busy || reason.trim().length < 3}>
            {busy ? 'Unlocking…' : cta}
          </Button>
        </div>
      </div>
    </div>
  );
}

function DeleteRunModal({ run, invoiceCount, busy, onCancel, onConfirm }) {
  const [confirmText, setConfirmText] = useStateRD('');
  const expected = 'DELETE';
  const canConfirm = confirmText.trim().toUpperCase() === expected && !busy;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 p-6">
      <div className="bg-white rounded-lg shadow-xl max-w-[520px] w-full overflow-hidden">
        <div className="px-6 py-5 border-b border-red-200 bg-red-50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-red-100 text-red-700 flex items-center justify-center"><Icon name="warn" size={16}/></div>
            <h3 className="text-[16px] font-semibold text-red-900">Delete {run.period}?</h3>
          </div>
        </div>
        <div className="px-6 py-4 space-y-3 text-[13px] text-neutral-700">
          <p>This permanently deletes the run and <b>all {invoiceCount.toLocaleString()} invoice{invoiceCount === 1 ? '' : 's'}</b> in it. Customer assignments are kept (they live on customer rows).</p>
          {run.status === 'finalized' && (
            <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-[12.5px]">
              <b>This run is finalized.</b> Deleting it removes the locked numbers a finance reviewer was about to sign off on. If you want to make changes instead, click <b>Unlock back to draft</b> from the run header.
            </p>
          )}
          <p className="text-neutral-500 text-[12px]">Approved runs can't be deleted — unlock them back to finalized first.</p>
          <div>
            <label className="block text-[12px] font-semibold text-neutral-700 mb-1.5">Type <span className="font-mono text-red-700">{expected}</span> to confirm</label>
            <input value={confirmText} onChange={e => setConfirmText(e.target.value)}
              className="w-full h-10 px-3 text-[14px] font-mono rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-700"
              placeholder={expected}/>
          </div>
        </div>
        <div className="px-6 py-4 flex items-center justify-end gap-2 border-t border-neutral-200 bg-canvas/60">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={!canConfirm}>
            {busy ? 'Deleting…' : 'Delete permanently'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function InvoiceDrawer({ invoice, onClose, onOverride, readOnly }) {
  const [overrideOpen, setOverrideOpen] = useStateRD(false);
  const [rejectOpen, setRejectOpen]     = useStateRD(false);
  const [newValue, setNewValue] = useStateRD(invoice?.commission?.toFixed(2) || '');
  const [reason, setReason] = useStateRD('');
  const [rejectReason, setRejectReason] = useStateRD('');
  const [busy, setBusy] = useStateRD(false);
  if (!invoice) return null;
  const rep = REPS.find(r => r.id === invoice.repId);
  // Approver context flowed in via the enriched payload from ScreenRunDetail.
  // When this drawer is opened from the rep view, _canReject is undefined → false.
  const canReject = !!invoice._canReject;
  const isRejected = !!invoice.rejected;
  // Original commission for the strikethrough display when rejected.
  const preRejection = invoice.preRejectionCommission ?? invoice.commission;
  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-neutral-900/40 animate-[fadeIn_0.2s_ease]" onClick={onClose}/>
      <div className="absolute top-0 right-0 bottom-0 w-[440px] bg-white shadow-2xl flex flex-col animate-[slideIn_0.2s_ease]">
        <div className="h-[56px] px-5 flex items-center justify-between border-b border-neutral-200">
          <div>
            <div className="text-[11px] text-neutral-500 uppercase tracking-[0.06em] font-semibold">Invoice</div>
            <div className="font-mono text-[15px] font-semibold text-neutral-900">{invoice.doc}</div>
          </div>
          <button className="text-neutral-500 hover:text-neutral-900 p-1 -mr-1" onClick={onClose}><Icon name="close" size={18}/></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {/* Identifying */}
          <div className="px-5 py-4 border-b border-neutral-200">
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-2">Identifying info</div>
            <dl className="grid grid-cols-2 gap-y-2 text-[13px]">
              <dt className="text-neutral-500">Client</dt>     <dd className="font-medium text-neutral-900 text-right">{invoice.client}</dd>
              <dt className="text-neutral-500">Branch</dt>      <dd className="font-mono text-neutral-900 text-right">{invoice.branch}</dd>
              <dt className="text-neutral-500">Invoice date</dt><dd className="font-mono text-neutral-900 text-right">{invoice.date}</dd>
              <dt className="text-neutral-500">Sales rep</dt>   <dd className="font-medium text-neutral-900 text-right">{rep?.name || '—'}</dd>
            </dl>
          </div>

          {/* Math */}
          <div className="px-5 py-4 border-b border-neutral-200">
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-3">Calculation</div>
            <div className="space-y-1.5 text-[13px]">
              <MathRow label="Total Bill"    value={invoice.totalBill}/>
              <MathRow label="Gross Wages" value={invoice.grossWages} subtract/>
              <div className="border-t border-dashed border-neutral-200 pt-1.5"/>
              <MathRow label="Gross Profit" value={invoice.gp} bold/>
              <MathRow label={`ACA (1.8% of wages)`}  value={invoice.aca}  subtract/>
              <MathRow label={`SICK (1.2% of wages)`} value={invoice.sick} subtract/>
              <div className="border-t border-dashed border-neutral-200 pt-1.5"/>
              <MathRow label="NET" value={invoice.net} bold/>
              <div className="flex items-center justify-between text-[13px] py-1">
                <span className="text-neutral-700">× Rate</span>
                <div className="flex items-center gap-2">
                  <Pill tone={invoice.tier === 'y1' ? 'y1' : 'y2'}>{invoice.tier === 'y1' ? 'Year 1' : 'Year 2+'}</Pill>
                  <span className="font-mono text-neutral-900">{(invoice.rate * 100).toFixed(1)}%</span>
                </div>
              </div>
              <div className="border-t-2 border-neutral-900 pt-2 mt-2"/>
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-semibold text-neutral-900">Commission</span>
                <div className="text-right">
                  {invoice.overridden && !isRejected && <div className="text-[11px] text-neutral-400 line-through font-mono">${invoice.originalCommission?.toFixed(2)}</div>}
                  {isRejected && <div className="text-[11px] text-neutral-400 line-through font-mono">${Number(preRejection).toFixed(2)}</div>}
                  <div className={`text-[20px] font-bold font-mono tabular-nums ${isRejected ? 'text-red-700' : 'text-neutral-900'}`}>
                    ${(isRejected ? 0 : invoice.commission).toFixed(2)}
                  </div>
                </div>
              </div>
              {invoice.overridden && !isRejected && (
                <div className="mt-2 px-3 py-2 bg-primary-100/60 border border-primary-200 rounded text-[12px] text-primary-900">
                  Overridden by V. Israni · {invoice.overrideReason}
                </div>
              )}
              {isRejected && (
                <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-[12px] text-red-900">
                  <div className="font-semibold mb-0.5">Rejected by approver</div>
                  <div className="text-red-800">{invoice.rejectionReason || '(no reason recorded)'}</div>
                  {invoice.rejectedAt && (
                    <div className="text-[10.5px] text-red-700 mt-1 font-mono">
                      {new Date(invoice.rejectedAt).toLocaleString()}
                    </div>
                  )}
                </div>
              )}
              {!isRejected && invoice.heldForCollection && !invoice.carriedOver && (
                <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-[12px] text-amber-900">
                  <div className="font-semibold mb-0.5">Held — pay on collection</div>
                  <div className="text-amber-800">
                    This invoice was unpaid in AR at the time the run was calculated, and the rep's plan holds commission until the customer pays.
                    Commission of <span className="font-mono">${Number(invoice.heldWouldBeCommission || invoice.preRejectionCommission || 0).toFixed(2)}</span> will be released
                    when the invoice is paid (the next AR upload that doesn't list it will carry it into that run).
                  </div>
                </div>
              )}
              {invoice.carriedOver && !invoice.isCarryover && (
                <div className="mt-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded text-[12px] text-emerald-900">
                  <div className="font-semibold mb-0.5">Released in a later run</div>
                  <div className="text-emerald-800">
                    This held invoice has been paid and the commission was carried forward as a line item in a subsequent commission run.
                  </div>
                </div>
              )}
              {invoice.isCarryover && (
                <div className="mt-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-[12px] text-blue-900">
                  <div className="font-semibold mb-0.5">Carryover from a previous run</div>
                  <div className="text-blue-800">
                    This invoice was originally billed earlier and held under a cash-basis plan because the customer hadn't paid.
                    It cleared in this period's AR upload, so the commission is being paid out now.
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* AR status */}
          <div className="px-5 py-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-2">AR status</div>
            {invoice.paid ? (
              <div className="flex items-center gap-2 text-[13px]">
                <Pill tone="paid">paid</Pill>
                <span className="text-neutral-700">Invoice settled in full</span>
              </div>
            ) : (
              <div className="space-y-1.5 text-[13px]">
                <div className="flex items-center gap-2"><Pill tone="unpaid">unpaid</Pill><span className="text-neutral-700">{invoice.ageDays} days outstanding</span></div>
                <div className="flex items-center justify-between pt-1">
                  <span className="text-neutral-500">Balance</span>
                  <Money value={invoice.outstanding} className="font-semibold text-amber-700"/>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        {(!readOnly || canReject) && (
          <div className="border-t border-neutral-200 p-4 bg-canvas/50">
            {/* Approver action — Reject (or Undo, if already rejected). Only shown
                when the run is finalized and the current user is approver-eligible. */}
            {canReject && !overrideOpen && !rejectOpen && (
              isRejected ? (
                <div className="space-y-2">
                  <div className="text-[12px] text-red-900 bg-red-50 border border-red-200 rounded px-3 py-2">
                    This invoice is rejected. The rep earns $0 on it for this run.
                  </div>
                  <Button variant="secondary" size="md" icon="unlock" className="w-full"
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      try { await invoice._onUnreject?.(invoice.id); onClose(); }
                      finally { setBusy(false); }
                    }}>
                    {busy ? 'Reverting…' : 'Undo rejection'}
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  {!readOnly && <Button variant="secondary" size="md" icon="override" className="flex-1" onClick={() => setOverrideOpen(true)}>Override</Button>}
                  <Button variant="destructive" size="md" icon="x" className="flex-1" onClick={() => { setRejectOpen(true); setRejectReason(''); }}>Reject invoice</Button>
                </div>
              )
            )}

            {/* Reject confirm panel */}
            {rejectOpen && (
              <div className="space-y-2.5">
                <div className="text-[12px] text-neutral-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  Rejecting an invoice sets the rep's commission on it to $0 for this run.
                  The rep will see the rejection and the reason on their dashboard.
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Reason (required, visible to rep)</label>
                  <textarea
                    rows="3" value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    placeholder="e.g. Rep wasn't on this account at the time of sale; commission belongs to onboarding bonus pool."
                    className="mt-1 w-full px-3 py-2 rounded-md border border-neutral-300 text-[13px]"/>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="md" className="flex-1" disabled={busy} onClick={() => setRejectOpen(false)}>Cancel</Button>
                  <Button variant="destructive" size="md" className="flex-1"
                    disabled={!rejectReason.trim() || busy}
                    onClick={async () => {
                      setBusy(true);
                      try {
                        await invoice._onReject?.(invoice.id, rejectReason.trim());
                        setRejectOpen(false); onClose();
                      } finally { setBusy(false); }
                    }}>
                    {busy ? 'Rejecting…' : 'Reject invoice'}
                  </Button>
                </div>
              </div>
            )}

            {/* Pre-finalize override flow (admin "tweak the number" with reason) */}
            {!readOnly && !canReject && !overrideOpen && (
              <div className="space-y-2">
                {invoice.excluded && (
                  <div className="text-[12px] text-neutral-700 bg-neutral-100 border border-neutral-200 rounded px-3 py-2">
                    This invoice is <b>excluded</b> from the rep's commission totals. Click Include to put it back.
                  </div>
                )}
                <div className="flex gap-2">
                  <Button variant="secondary" size="md" icon="override" className="flex-1" onClick={() => setOverrideOpen(true)}>Override</Button>
                  {invoice._canExclude && invoice._onToggleExclude && (
                    invoice.excluded ? (
                      <Button variant="secondary" size="md" icon="check" className="flex-1" disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          try { await invoice._onToggleExclude(invoice.id, false); onClose(); }
                          finally { setBusy(false); }
                        }}>
                        {busy ? 'Including…' : 'Include'}
                      </Button>
                    ) : (
                      <Button variant="ghost" size="md" className="flex-1" icon="x" disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          try { await invoice._onToggleExclude(invoice.id, true); onClose(); }
                          finally { setBusy(false); }
                        }}>
                        {busy ? 'Excluding…' : 'Exclude'}
                      </Button>
                    )
                  )}
                </div>
              </div>
            )}
            {overrideOpen && (
              <div className="space-y-2.5">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">New commission value</label>
                  <input value={newValue} onChange={e => setNewValue(e.target.value)} className="mt-1 w-full h-9 px-3 rounded-md border border-neutral-300 text-[14px] font-mono"/>
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Reason (required)</label>
                  <textarea rows="2" value={reason} onChange={e => setReason(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-neutral-300 text-[13px]"/>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="md" className="flex-1" onClick={() => setOverrideOpen(false)}>Cancel</Button>
                  <Button variant="primary" size="md" className="flex-1" disabled={!reason} onClick={() => { onOverride(invoice.id, parseFloat(newValue), reason); setOverrideOpen(false); onClose(); }}>Apply override</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function MathRow({ label, value, subtract, bold }) {
  return (
    <div className={`flex items-center justify-between text-[13px] py-0.5 ${bold ? 'font-semibold text-neutral-900' : 'text-neutral-700'}`}>
      <span>{label}</span>
      <span className="font-mono tabular-nums">{subtract ? <span className="text-neutral-500 mr-1">−</span> : null}${Math.abs(value).toFixed(2)}</span>
    </div>
  );
}

// ===================== S11 · Customers list =====================
export function ScreenCustomers({ onNav, currentUser }) {
  const [customers, setCustomers] = useStateRD([]);
  const [reps, setReps]           = useStateRD([]);
  const [loading, setLoading]     = useStateRD(true);
  const [query, setQuery]         = useStateRD('');
  const [repFilter, setRepFilter] = useStateRD('all');
  const [saving, setSaving]       = useStateRD(null); // customerId being saved

  useEffectRD(() => {
    let alive = true;
    (async () => {
      try {
        const [cs, us] = await Promise.all([api.customers.list(), api.users.list()]);
        if (!alive) return;
        setCustomers(cs);
        setReps(us.filter(u => u.role === 'rep' && u.active));
      } catch (err) { console.error('Customers load:', err); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  // Customer-rep assignment is gated on customers.assign_rep, not on the
  // legacy admin base role. Approver has only customers.read, so she can
  // view but not change the rep dropdown.
  const isAdmin = hasPermission(currentUser, 'customers', 'assign_rep');
  const canEditFirstInvoice = hasPermission(currentUser, 'customers', 'edit');
  const repById = new Map(reps.map(r => [r.id, r]));

  const filtered = customers.filter(c => {
    if (query && !c.name.toLowerCase().includes(query.toLowerCase())) return false;
    if (repFilter === 'all') return true;
    if (repFilter === 'unassigned') return !c.repUserId;
    return c.repUserId === repFilter;
  });
  const unassignedCount = customers.filter(c => !c.repUserId).length;

  const changeRep = async (customerId, repUserId) => {
    setSaving(customerId);
    try {
      const updated = await api.customers.update(customerId, { repUserId: repUserId || null });
      setCustomers(cs => cs.map(c => c.id === customerId ? updated : c));
    } catch (err) { alert('Could not update: ' + err.message); }
    finally { setSaving(null); }
  };

  // Edit the first_invoice anchor on a customer. This is the date used by
  // pickTierForInvoice to decide Y1 vs Y2+. Editing it affects future
  // calculations — already-calculated runs need Recalculate to pick it up.
  const changeFirstInvoice = async (customerId, isoDate) => {
    setSaving(customerId);
    try {
      const updated = await api.customers.update(customerId, { firstInvoice: isoDate });
      setCustomers(cs => cs.map(c => c.id === customerId ? updated : c));
    } catch (err) { alert('Could not update first invoice date: ' + err.message); }
    finally { setSaving(null); }
  };

  if (loading) return <div className="p-10 text-center text-neutral-500 text-[13px]">Loading customers…</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-[24px] font-bold text-neutral-900 tracking-[-0.01em]">Customers</h1>
          <div className="text-[13px] text-neutral-500 mt-1">Permanent directory of every client that has appeared in a run. {isAdmin && 'Assignments here persist across runs.'}</div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Input icon="search" value={query} onChange={setQuery} placeholder="Search clients…" className="w-72"/>
        <Select value={repFilter} onChange={setRepFilter} options={[
          { value: 'all', label: 'All reps' },
          { value: 'unassigned', label: '— Unassigned —' },
          ...reps.map(r => ({ value: r.id, label: r.name })),
        ]}/>
        <div className="ml-auto text-[12px] text-neutral-500">
          <b className="text-neutral-900">{filtered.length}</b> of {customers.length} customers
          {unassignedCount > 0 && <> · <span className="text-amber-700 font-semibold">{unassignedCount} unassigned</span></>}
        </div>
      </div>

      {customers.length === 0 && (
        <Banner tone="info" icon="info" title="No customers yet">
          Customers are auto-created when you upload a Transaction file in the New Run wizard.
        </Banner>
      )}

      {customers.length > 0 && (
        <Card padding="p-0">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-neutral-500 bg-canvas/60 border-b border-neutral-200">
                <th className="px-4 py-2.5 font-semibold">Client</th>
                <th className="px-4 py-2.5 font-semibold">Root ID</th>
                <th className="px-4 py-2.5 font-semibold">Branch</th>
                <th className="px-4 py-2.5 font-semibold">First invoice</th>
                <th className="px-4 py-2.5 font-semibold w-[260px]">Sales rep</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-[13px] text-neutral-500">No customers match the current filters.</td></tr>
              )}
              {filtered.map(c => {
                const rep = c.repUserId ? repById.get(c.repUserId) : null;
                return (
                  <tr key={c.id} className="hover:bg-primary-100/30">
                    <td className="px-4 py-3 font-semibold text-neutral-900">{c.name}</td>
                    <td className="px-4 py-3 font-mono text-neutral-700">{c.rootId || '—'}</td>
                    <td className="px-4 py-3 font-mono text-neutral-700">{c.branch || '—'}</td>
                    <td className="px-4 py-3">
                      {canEditFirstInvoice ? (
                        <input
                          type="date"
                          value={c.firstInvoice || ''}
                          onChange={(e) => changeFirstInvoice(c.id, e.target.value || null)}
                          disabled={saving === c.id}
                          className="h-8 px-2 text-[12px] font-mono rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 disabled:opacity-50"
                          title="Editing first invoice changes Y1/Y2 tier resolution. Recalculate the run after editing."/>
                      ) : (
                        <span className="font-mono text-neutral-500">{c.firstInvoice || '—'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isAdmin ? (
                        <Select size="sm" value={c.repUserId || ''} onChange={v => changeRep(c.id, v)}
                          placeholder="Choose rep…"
                          options={[
                            { value: '', label: '— Unassigned —' },
                            ...reps.map(r => ({ value: r.id, label: r.name })),
                          ]}/>
                      ) : rep ? (
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full bg-neutral-100 text-neutral-700 flex items-center justify-center text-[9px] font-bold">{rep.name.split(' ').map(s=>s[0]).join('')}</div>
                          <span className="text-neutral-800">{rep.name}</span>
                        </div>
                      ) : <Pill tone="warn">Unassigned</Pill>}
                      {saving === c.id && <span className="ml-2 text-[11px] text-neutral-500">saving…</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ===================== S14 · Rates =====================
export function ScreenRates({ tenant, onTenantSaved, currentUser }) {
  const defaults = { aca: 0.018, sick: 0.012, y1: 0.05, y2: 0.07 };
  const r = (tenant && tenant.rates) || defaults;
  const toPct = (n) => (n * 100).toString();

  // State: live % strings for each input.
  const [aca,  setAca]  = useStateRD(toPct(r.aca));
  const [sick, setSick] = useStateRD(toPct(r.sick));
  const [y1,   setY1]   = useStateRD(toPct(r.y1));
  const [y2,   setY2]   = useStateRD(toPct(r.y2));
  const [editing, setEditing] = useStateRD(false);
  const [saving, setSaving] = useStateRD(false);
  const [saved,  setSaved]  = useStateRD(false);
  const [error,  setError]  = useStateRD(null);

  // Reset state when the tenant prop changes.
  useEffectRD(() => {
    setAca(toPct(r.aca)); setSick(toPct(r.sick));
    setY1(toPct(r.y1));   setY2(toPct(r.y2));
    setSaved(false); setError(null); setEditing(false);
  }, [tenant?.id]);

  const isAdmin = currentUser?.role === 'admin';

  // Detect dirty state for Save button enablement.
  const dirty = (
    parseFloat(aca)  !== r.aca  * 100 ||
    parseFloat(sick) !== r.sick * 100 ||
    parseFloat(y1)   !== r.y1   * 100 ||
    parseFloat(y2)   !== r.y2   * 100
  );

  const parsePct = (s) => {
    const n = parseFloat(s);
    return isFinite(n) && n >= 0 && n <= 100 ? n / 100 : null;
  };

  const save = async () => {
    setError(null);
    const a = parsePct(aca), s = parsePct(sick), y1v = parsePct(y1), y2v = parsePct(y2);
    if ([a, s, y1v, y2v].some(v => v === null)) {
      setError('All rates must be numbers between 0 and 100.');
      return;
    }
    setSaving(true);
    try {
      const newRates = { aca: a, sick: s, y1: y1v, y2: y2v };
      const updated = await api.tenants.update(tenant.id, { rates: newRates });
      if (onTenantSaved) onTenantSaved(updated);
      setSaved(true);
      setEditing(false);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Save failed: ' + err.message);
    } finally { setSaving(false); }
  };

  const cancel = () => {
    setAca(toPct(r.aca)); setSick(toPct(r.sick));
    setY1(toPct(r.y1));   setY2(toPct(r.y2));
    setError(null); setSaved(false); setEditing(false);
  };

  const fields = [
    { key: 'ACA',  value: aca,  set: setAca,  tone: 'neutral', label: 'ACA',  desc: 'Affordable Care Act — applied to Gross Wages' },
    { key: 'SICK', value: sick, set: setSick, tone: 'neutral', label: 'SICK', desc: 'Sick leave accrual — applied to Gross Wages' },
    { key: 'Y1',   value: y1,   set: setY1,   tone: 'y1',      label: 'Y1',   desc: 'Commission % in the client\'s first 12 months' },
    { key: 'Y2+',  value: y2,   set: setY2,   tone: 'y2',      label: 'Y2+',  desc: 'Commission % after the first year' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-[24px] font-bold text-neutral-900 tracking-[-0.01em]">Rates &amp; settings</h1>
          <div className="text-[13px] text-neutral-500 mt-1">
            Tenant: <span className="font-semibold text-neutral-800">{tenant ? tenant.name : '—'}</span>
            {!isAdmin && <span className="ml-2">· Read-only (admins can edit)</span>}
            {isAdmin && <span className="ml-2">· Changes apply to new runs. Historical runs keep their rate snapshot.</span>}
          </div>
        </div>
      </div>

      {!isAdmin && (
        <Banner tone="info" icon="lock" title="Read-only">
          Only admins can change tenant rates. Contact your Finance / Ops admin if this needs to change.
        </Banner>
      )}

      <Section title="Commission rate structure"
        sub="Tenant defaults — each rep can override Y1 and Y2+ individually in Users."
        right={isAdmin && !editing ? (
          <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
            <span className="inline-flex items-center gap-1.5"><Icon name="override" size={13}/> Edit rates</span>
          </Button>
        ) : null}>
        <div className="grid grid-cols-4 gap-4">
          {fields.map(f => (
            <Card key={f.key} padding="p-5">
              <div className="flex items-center justify-between">
                <Pill tone={f.tone}>{f.label}</Pill>
              </div>
              {editing ? (
                <div className="mt-3 relative">
                  <input type="number" step="0.01" min="0" max="100" value={f.value}
                    disabled={saving} autoFocus={f.key === 'ACA'}
                    onChange={e => f.set(e.target.value)}
                    className="w-full h-12 pl-3 pr-9 text-[28px] font-bold font-mono tabular-nums text-neutral-900 rounded-md border border-primary-300 bg-primary-100/20 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 disabled:opacity-50"/>
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[20px] text-neutral-400 font-mono font-bold">%</span>
                </div>
              ) : (
                <div className="mt-3 text-[32px] font-bold font-mono tabular-nums text-neutral-900 leading-none">
                  {parseFloat(f.value || 0).toFixed(2)}<span className="text-[20px] text-neutral-400 ml-1">%</span>
                </div>
              )}
              <div className="text-[12px] text-neutral-500 mt-2 leading-relaxed">{f.desc}</div>
            </Card>
          ))}
        </div>

        {editing && (
          <Card padding="p-4" className="mt-4 border-primary-300 bg-primary-100/10">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[13px]">
                {error ? <span className="text-red-700">{error}</span>
                  : dirty ? <span className="text-amber-700">You have unsaved changes</span>
                  : <span className="text-neutral-500">No changes yet</span>}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={cancel} disabled={saving}>Cancel</Button>
                <Button variant="primary" size="sm" icon="check" onClick={save} disabled={!dirty || saving}>
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {!editing && saved && (
          <div className="mt-3 text-[13px] text-emerald-700 flex items-center gap-1.5">
            <Icon name="check" size={14}/> Saved
          </div>
        )}
      </Section>

      <Section title="Formula" sub="Same math applied to every invoice.">
        <Card padding="p-5">
          <div className="font-mono text-[13px] text-neutral-700 space-y-1">
            <div><span className="text-neutral-500">net        = </span> grossProfit − (grossWages × ACA) − (grossWages × SICK)</div>
            <div><span className="text-neutral-500">tier       = </span> Y1 if invoice date is within 12 months of client's first invoice, else Y2+</div>
            <div><span className="text-neutral-500">rate       = </span> rep's own Y1/Y2+ override if set, else tenant default above</div>
            <div className="text-primary-700 font-semibold"><span className="text-neutral-500">commission = </span> net × rate</div>
          </div>
        </Card>
      </Section>
    </div>
  );
}

